import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Router, { useRouter } from "next/router";
import { LocalizationProvider, Localized } from "@fluent/react";

import useErrorConsumer from "@kikoff/hooks/src/useErrorConsumer";
import { web } from "@kikoff/proto/src/protos";
import { format } from "@kikoff/utils/src/string";

import Detail from "@component/dashboard/account/detail";
import PageAlign from "@component/dashboard/offer/page_align";
import SectionCard from "@component/dashboard/offer/section_card";
import PaymentPlan from "@component/dashboard/store/payment_plan";
import Line from "@component/line";
import { GenericSpinner } from "@component/loading_indicators/loading_indicators";
import StreamlinedHeader from "@component/onboarding/streamlined_header";
import ProductEntry from "@component/shopping/product_entry";
import {
  fetchPaymentMethods,
  selectHasPaymentMethod,
  selectUpfrontPaymentWallet,
} from "@feature/funds";
import {
  selectCurrentSubscriptionProduct,
  updateOrders,
} from "@feature/shopping";
import {
  getKikoffCreditAccountMentalModelName,
  useCreditServiceGenericMentalModelName,
} from "@src/constants";
import { track } from "@util/analytics";
import { getLocaleCookie, l10nResources } from "@util/l10n";

import styles from "./styles.module.scss";

const l10n = l10nResources({
  en: `order-confirmation-next-payment-note = * Your minimum payment due may be {$formattedMinimumPayment} or {$minimumPaymentPercent} of your total account balance, whichever is greater.
order-confirmation-order-number = ORDER #
paid-today = Paid Today
scheduled-payment-date = Scheduled payment date
next-payment-due-date = Next payment due date
next-payment = Next payment
payment-due = Payment due
check-your-email-for-confirmation-and-instructions = Check your email for confirmation and instructions regarding your purchase.
repayment-overview = Repayment Overview
order-confirmation-here-is-repayment-schedule-note = Here is your updated repayment schedule. We’ll email you before your next payment is due.
purchase-summary = Purchase Summary
congrats-opened-credit-account = Congrats on opening {$your}{$kikoffCreditAccount}!
your-order-has-been-placed = Congrats! Your order has been placed
order-confirmation-downloadable-note = You can download your item using the link below or in your Order Center anytime
order-confirmation-report-to-bureaus-note = Your {$kikoffCreditAccount} has been opened and was used to pay for {$creditService}.
kikoff-cash-card-intro = Introducing the Kikoff Secured Credit Card
kikoff-cash-card-text = Get the best perks of a banking service and all the power of credit building.
kikoff-cash-card-link = Learn more
final-step-header = Final step
final-step-is-payable = Build positive payment history and unlock the Credit Builder Loan by making your first payment today
final-step-is-not-payable = Link an account and review your projected repayment schedule
final-step-note-on-time = It’s really important to make every monthly payment on-time.
final-step-note-payment-history = Each payment builds positive payment history. And missing a payment could hurt your credit!
`,
  es: `order-confirmation-next-payment-note = * Su pago mínimo adeudado puede ser {$formattedMinimumPayment} o {$minimumPaymentPercent} del saldo total de su cuenta, el que sea mayor.
order-confirmation-order-number = ORDENAR #
paid-today = Pagado hoy
scheduled-payment-date = Fecha de pago programada
next-payment-due-date = Próxima fecha de vencimiento del pago
next-payment = Siguiente pago
payment-due = Fecha de pago
check-your-email-for-confirmation-and-instructions = Revise su correo electrónico para obtener confirmación e instrucciones con respecto a su compra.
repayment-overview = Resumen de Reembolso
order-confirmation-here-is-repayment-schedule-note = Aquí está su calendario de pago actualizado. Le enviaremos un correo electrónico antes de que venza su próximo pago.
purchase-summary = Resumen de Compra
congrats-opened-credit-account = ¡Felicidades por abrir {$your}{$kikoffCreditAccount}!
your-order-has-been-placed = ¡Felicitaciones! Su orden ha sido puesta
order-confirmation-downloadable-note = Puede descargar su artículo usando el enlace a continuación o en su Centro de pedidos en cualquier momento
order-confirmation-report-to-bureaus-note = Su {$kikoffCreditAccount} se ha abierto y se utilizó para configurar su plan de {$creditService}.
kikoff-cash-card-intro = Presentamos la tarjeta de crédito asegurada Kikoff
kikoff-cash-card-text = Obtenga las mejores ventajas de un servicio bancario y todo el poder de la creación de crédito.
kikoff-cash-card-link = Aprende más
final-step-header = Último paso
final-step-is-payable = Desarrolle un historial de pago positivo y desbloquee el Préstamo Credit Builder al hacer su primer pago hoy
final-step-is-not-payable = Vincule una cuenta y revise su cronograma de pago proyectado
final-step-note-on-time = Es muy importante hacer todos los pagos mensuales a tiempo.
final-step-note-payment-history = Cada pago genera un historial de pagos positivo. ¡Y la falta de un pago podría dañar su crédito!
`,
});

function postPurchaseSuggestedAction({
  orderItem,
  order,
  selectedSubscriptionPlan,
}) {
  if (selectedSubscriptionPlan !== null) {
    return null;
  }

  if (orderItem.product.type === web.public_.Product.Type.COURSE) {
    return {
      url: `/dashboard/course/${order.token}`,
      text: "View course",
    };
  }
  if (orderItem.product.type === web.public_.Product.Type.BANZAI_GOAL_PACKAGE) {
    return {
      url: `/dashboard/pack/${order.token}`,
      text: "View pack",
    };
  }
  return (
    orderItem.downloadUrl && {
      url: orderItem.downloadUrl,
      text: "Download now",
    }
  );
}

function OrderHeader({ activating, order, product }) {
  const user = useSelector((state) => state.user);
  const locale = getLocaleCookie();

  const userOrderInfo = ` | ${user.proto.info.email} | ${user.proto.info.givenName} ${user.proto.info.surname}`;

  const headerKey = activating
    ? "congrats-opened-credit-account"
    : "your-order-has-been-placed";
  const your = locale === "es" ? "su " : "your ";

  const kikoffCreditAccount = getKikoffCreditAccountMentalModelName();
  const creditService = useCreditServiceGenericMentalModelName();

  const noteKey = ((): string => {
    const downloadable = product.type === web.public_.Product.Type.BOOK;
    if (downloadable && !activating) {
      return "order-confirmation-downloadable-note";
    }

    return "order-confirmation-report-to-bureaus-note";
  })();

  return (
    <>
      <div className={styles["order-number"]}>
        <Localized id="order-confirmation-order-number" />
        <span data-cy="order-token">{order?.token}</span>
        {userOrderInfo}
      </div>
      <div className={styles["header-and-note"]}>
        <div className="text:heading-4">
          {l10n.getString(headerKey, { kikoffCreditAccount, your })}
        </div>
        <div className="text:regular">
          {l10n.getString(noteKey, { kikoffCreditAccount, creditService })}
        </div>
      </div>
    </>
  );
}

function OrderConfirmationView({ activating }) {
  const dispatch = useDispatch();
  const error = useErrorConsumer();
  const router = useRouter();

  const orders = useSelector((state) => state.shopping.orders);
  const creditLine = useSelector((state) => state.creditLine.account);
  const hasPaymentMethod = useSelector(selectHasPaymentMethod());
  const paidUpfrontWallet = useSelector(selectUpfrontPaymentWallet());
  const products = useSelector((state) => state.shopping.products);
  const [currentSubscriptionProduct] = useSelector(
    selectCurrentSubscriptionProduct.load()
  );
  const caPaymentSetUpAfterOpen = useSelector(
    (state) => state.shopping.caPaymentSetUpAfterOpen
  );

  const checkedOutOrder = useSelector(
    (state) => state.shopping.checkoutResponse?.order
  );
  const selectedSubscriptionPlan = useSelector(
    (state) => state.subscription.selectedPlan
  );

  useEffect(() => {
    dispatch(fetchPaymentMethods());
  }, []);

  useEffect(() => {
    if (!orders) {
      dispatch(updateOrders()).catch((err: Error) => {
        error.throw(err.message);
      });
    }

    if (!hasPaymentMethod) {
      dispatch(fetchPaymentMethods()).catch((err: Error) => {
        error.throw(err.message);
      });
    }
  }, []);

  if (orders === null) {
    return (
      <div className={styles["spinner-wrapper"]}>
        <GenericSpinner center />
      </div>
    );
  }

  // In case of user not having any orders. Use same behavior as using no token
  // at all in PostPurchaseOrderRoute and drop user on dashboard.
  if (orders.length === 0) {
    track("Store - Order Confirmation - User Has No Orders");
    Router.push("/dashboard");
    return null;
  }

  const orderByTokenParam = orders.find((o) => o.token === router.query.token);
  const displayableOrders = orders.filter(
    (userOrder) => userOrder.hideOrder === false
  );
  const order =
    displayableOrders.find(
      (userOrder) => userOrder.token === checkedOutOrder?.token
    ) ||
    checkedOutOrder ||
    orderByTokenParam;

  // In case of user having orders, but using an invalid token. Use same
  // behavior as using no token at all in PostPurchaseOrderRoute and drop user
  // onto account orders page.
  if (!order && selectedSubscriptionPlan === null) {
    track("Store - Order Confirmation - Order Not Found");
    Router.push("/dashboard/credit-account/settings/orders");
    return null;
  }

  const isPayable = creditLine.outstandingMinimumPaymentCents > 0;

  const orderItem = order?.orderItems[0];

  // If selectedSubscriptionPlan is set then user is going through new subscription onboarding
  // flow so order will not be present.
  let product;
  if (selectedSubscriptionPlan !== null) {
    product = products.find((product) => {
      return (
        product.plan === selectedSubscriptionPlan &&
        product.subscriptionDurationMonths === 12
      );
    });
  } else {
    product = orderItem.product;
  }

  if (!product && caPaymentSetUpAfterOpen) {
    product = currentSubscriptionProduct;
  }

  const formattedMinimumPayment = format.money(product?.monthlyPaymentCents);
  const minimumPaymentPercent = `${product?.creditLineAgreement.minimumPaymentAmountPercents}%`;

  const paidTodayCents = (() => {
    if (paidUpfrontWallet) {
      return product.payInFullPriceCents;
    }

    return (
      creditLine.transactions.find(
        (transaction) => transaction.transactionTitle === "Payment"
      )?.amountCents || 0
    );
  })();

  return (
    <PageAlign className={styles["order-confirmation-view"]}>
      <StreamlinedHeader />
      <LocalizationProvider l10n={l10n}>
        <OrderHeader activating={activating} order={order} product={product} />
        <SectionCard
          header={l10n.getString("repayment-overview")}
          className={styles["repayment-overview"]}
        >
          <Detail label={l10n.getString("paid-today")}>
            {format.money(paidTodayCents)}
          </Detail>
          {paidUpfrontWallet && (
            <Detail label="Store credits applied today">
              {format.money(product.monthlyPaymentCents)}
            </Detail>
          )}
          {creditLine.delayedPayment ? (
            <Detail label={l10n.getString("scheduled-payment-date")}>
              {format.date(creditLine.delayedPayment.scheduledAt, "mm/dd/YYYY")}
            </Detail>
          ) : (
            <Detail label={l10n.getString("next-payment-due-date")}>
              {format.date(creditLine.paymentDueDate, "mm/dd/YYYY")}
            </Detail>
          )}
          <Detail label={l10n.getString("next-payment") + "*"}>
            {format.money(creditLine.paymentSchedule.monthlyPaymentCents)}
          </Detail>
          <div className={styles["next-payment-note"]}>
            <Localized
              id="order-confirmation-next-payment-note"
              vars={{ formattedMinimumPayment, minimumPaymentPercent }}
            />
          </div>
        </SectionCard>

        <SectionCard
          header={l10n.getString("purchase-summary")}
          className={styles["purchase-summary"]}
        >
          <ProductEntry
            widen
            product={product}
            className={styles.product}
            action={postPurchaseSuggestedAction({
              orderItem,
              order,
              selectedSubscriptionPlan,
            })}
          />
          <div className={styles["check-email-note"]}>
            <Localized id="check-your-email-for-confirmation-and-instructions" />
          </div>
        </SectionCard>

        <Line className={styles.line} />

        {hasPaymentMethod ? (
          <>
            <p className={styles["payment-plan-note"]}>
              <Localized id="order-confirmation-here-is-repayment-schedule-note" />
            </p>
            <div className={styles["payment-schedule-wrapper"]}>
              <PaymentPlan paymentSchedule={creditLine.paymentSchedule} />
            </div>
          </>
        ) : (
          <div className={styles["final-step"]}>
            <header className={styles["final-step-header"]}>
              {l10n.getString("final-step-header")}
            </header>
            <div className={styles["final-step-subtitle"]}>
              {isPayable
                ? l10n.getString("final-step-is-payable")
                : l10n.getString("final-step-is-not-payable")}
            </div>
            <div className={styles.note}>
              {l10n.getString("final-step-note-on-time")}
              <br />
              <br />
              {l10n.getString("final-step-note-payment-history")}
            </div>
          </div>
        )}
        <div className={styles.padBottom} />
      </LocalizationProvider>
    </PageAlign>
  );
}

export default OrderConfirmationView;
